import './section-card-list.scss';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@sola/ark-core/molecules/tooltip/tooltip.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';

type SectionCardListHeadingProps =
  | {
      isLoadingHeading: true;
      heading?: never;
      subheading?: never;
      tooltipContent?: never;
    }
  | {
      isLoadingHeading?: false;
      heading: string;
      subheading?: string | React.ReactNode;
      tooltipContent?: string;
    };

type SectionCardListContentProps =
  | {
      isLoadingCards: true;
      children?: React.ReactNode;
    }
  | {
      isLoadingCards?: false;
      children: React.ReactNode;
    };

export type SectionCardListProps = SectionCardListHeadingProps &
  SectionCardListContentProps;

/**
 * Section for Card Lists.
 * In the future, we may want to make this more composable depending on our needs.
 */
export const SectionCardList: React.FC<SectionCardListProps> = ({
  children,
  heading,
  tooltipContent,
  subheading,
  isLoadingHeading,
  isLoadingCards,
}) => {
  return (
    <div className="max-w-md">
      <div className="SectionCardList lg:flex lg:items-start lg:flex-row lg:justify-between lg:gap-comfy-xl">
        <div className="SectionCardList__labels mb-comfy-xl lg:mb-none shrink-0">
          {!isLoadingHeading && (
            <>
              <div className="flex items-center label-potent label-md gap-snug-lg label-neutral-mild">
                <h2 className="label-potent label-md label-neutral-mild">
                  {heading}
                </h2>

                {tooltipContent && (
                  <Tooltip>
                    <TooltipTrigger>
                      <button
                        type="button"
                        className="SectionCardList__tticon flex items-center justify-center"
                        aria-label="Additional information"
                        onClick={(e) => {
                          e.preventDefault();
                        }}
                      >
                        <ArkIcon iconName="info-circle" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      className="label-md lg:label-lg"
                      alignOffset={-12}
                      onPointerDownOutside={(e) => {
                        e.preventDefault();
                      }}
                    >
                      {tooltipContent}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              {subheading && (
                <span className="label-moderate label-lg label-neutral-amplified">
                  {subheading}
                </span>
              )}
            </>
          )}

          {isLoadingHeading && <Skeleton height={50} />}
        </div>
        <div className="SectionCardList__cards w-full flex flex-col gap-comfy-xs">
          {isLoadingCards ? <Skeleton height={180} /> : children}
        </div>
      </div>
    </div>
  );
};
